import React, { Suspense, lazy, useEffect, useState } from 'react'
import api from '../../utils/api'
import LoadingPage from '../../pages/LoadingPage'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Chip, Typography } from '@mui/material'
import { notifyError } from '../../utils/notifyError'
import Button from '@mui/material/Button'
import { notification, closeNotification } from '../../components/Notifications'
import { type ITask, type ITaskList } from '../../models/Task'
import { GridSortItem } from '@mui/x-data-grid'
import { TaskResultStatus } from '../SetupWizardFeature/styles'
import { revokeTask } from '../../APIManager/TaskManager'
import GenericModal from '../../components/GenericModal'
import { ButtonType } from '../../components/GenericButton/styles'
import { Actions, type Row } from '../../components/CustomTable/types'

const AppTable = lazy(async () => await import('../../components/CustomTable'))

export const capitalize = (word: string): string => {
  const capitalized =
    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
  return capitalized
}

export type ChipColor = 'secondary' | 'primary' | 'error' | 'success' | 'warning'
export const taskDownload = async (taskId: string): Promise<void> => {
  const toastKey = notification('Preparing download...', { variant: 'loading', persist: true })
  try {
    const result = await api.get(`/api/tasks/${taskId}/download/`, { responseType: 'blob' })
    const href = URL.createObjectURL(result.data)

    // create "a" HTML element with href to file & click
    const link = document.createElement('a')
    link.href = href
    link.setAttribute('download', `task-${taskId}.csv`)
    document.body.appendChild(link)
    link.click()

    // clean up "a" element & remove ObjectURL
    document.body.removeChild(link)
    URL.revokeObjectURL(href)
    closeNotification(toastKey)
    notification('Task information downloaded', { variant: 'success' })
  } catch {
    closeNotification(toastKey)
    notification('Failed to download Task information', { variant: 'error' })
  }
}

/** The row fields the stop-task flow needs, read off the CDG row's `data`. */
interface StoppableTask {
  taskId: string
  taskName: string | null
}

const TaskFeature = (): JSX.Element => {
  const queryClient = useQueryClient()
  const [taskToStop, setTaskToStop] = useState<StoppableTask | null>(null)
  const [params, setParams] = useState<Record<string, any>>({
    sort: JSON.stringify({
      field: 'start_timestamp',
      direction: 'desc'
    })
  })

  const { data: studentList, isLoading, isError: isTasksError, error: tasksError } = useQuery({
    queryKey: ['tasks', params],
    queryFn: async (): Promise<ITaskList> => {
      const result = await api.get<ITaskList>('/api/tasks/', {
        params,
        headers: {
          Accept: 'application/cdg'
        }
      })
      return result.data
    }
  })

  useEffect(() => {
    if (isTasksError) notifyError(tasksError, { fallbackMessage: 'Failed to load tasks. Please try again.' })
  }, [isTasksError])

  const { mutate: stopTask, isPending: isStoppingTask } = useMutation({
    mutationFn: async (taskId: string) => await revokeTask(taskId),
    // Handled locally so the message names the task; keeps the global mutation
    // notifier from firing a second, generic toast.
    meta: { silent: true },
    onSuccess: () => {
      notification(
        taskToStop?.taskName ? `Stopping "${taskToStop.taskName}".` : 'Stopping the task.',
        { variant: 'success' }
      )
      setTaskToStop(null)
      void queryClient.invalidateQueries({ queryKey: ['tasks'] })
    },
    onError: (error: unknown) => {
      setTaskToStop(null)
      notifyError(error, { fallbackMessage: 'Failed to stop the task. Please try again.' })
    }
  })

  // The backend sends EDIT on an Activity Monitor row to mean "this task has not
  // resolved and can be stopped" (CR-284). Rows it does not mark stay READ-only.
  const extraRowActions = [{
    action: Actions.EDIT,
    label: 'Stop Task',
    destructive: true,
    onClick: (row: Row) => {
      // task_name is nullable — tasks that died before setting a type have none.
      setTaskToStop({
        taskId: row?.data?.taskId,
        taskName: row?.data?.taskName ?? null
      })
    }
  }]

  const columns = [...(studentList?.keys ?? []), {
    field: 'task_id_download', headerName: 'Download', shown: true, allowedSearch: false
  }]
  const rows = studentList?.data ?? []
  const tableHeader = studentList?.header?.headerName ?? ''

  const overrides = {
    taskIdDownload: (row: ITask) => {
      return <Button disabled={row.status !== 'COMPLETED'} onClick={() => { void taskDownload(row.taskId) }}>Download</Button>
    },
    status: (row: ITask) => {
      const status = row.status
      let color: ChipColor = 'primary'

      switch (status) {
        case 'STARTED':
          color = 'primary'
          break
        case 'COMPLETED':
          color = 'success'
          break
        case 'FAILED':
          color = 'error'
          break
        case 'REVOKED':
          color = 'warning'
          break
      }
      return <Chip
        label={capitalize(status)}
        size='small'
        color={color}
        variant={'outlined'} />
    },
    result: (row: ITask) => {
      const result = row.result
      let color: ChipColor = 'primary'

      switch (result) {
        case TaskResultStatus.ERROR:
          color = 'error'
          break
        case TaskResultStatus.SUCCESSFUL:
          color = 'success'
          break
        case TaskResultStatus.PENDING:
          color = 'warning'
          break
        case 'WARNING':
          color = 'warning'
          break
      }
      return <Chip
        label={capitalize(result)}
        size='small'
        color={color}
        variant={'outlined'} />
    }
  }

  return (
    <Suspense fallback={<LoadingPage />}>
    <>
      <AppTable
        topbarTitle={tableHeader}
        list={studentList}
        rows={rows}
        loading={isLoading}
        columns={columns}
        featureName="Tasks"
        params={params}
        setParams={setParams}
        actionModalEntityDisplayField='task'
        disableRowDoubleClick={true}
        overrides={overrides}
        hideRowEditAction={true}
        extraRowActions={extraRowActions}
      />
      <GenericModal
        isOpen={taskToStop !== null}
        onClose={() => { setTaskToStop(null) }}
        title='Stop this task?'
        confirmActionText='Stop Task'
        confirmActionType={ButtonType.Destructive}
        confirmButtonLoading={isStoppingTask}
        onConfirm={async () => { if (taskToStop) stopTask(taskToStop.taskId) }}
      >
        <Typography variant='body2'>
          {taskToStop?.taskName ? `"${taskToStop.taskName}" will stop running.` : 'This task will stop running.'}
          {' '}Work it already finished is kept; anything still in progress is dropped.
          This cannot be undone.
        </Typography>
      </GenericModal>
    </>
    </Suspense>
  )
}

export default TaskFeature
